/**
 * One target's Elo from each run that identified it, as discrete marks.
 *
 * **Deliberately not a line chart, and this is the whole design of the component.**
 * Elo is comparative *within* a run: it says this hypothesis beat that one in one
 * tournament, against whichever opponents that sweep happened to generate. Two runs'
 * ratings come from two different pools, so they are not two points on one series. A
 * polyline through them draws a trend that does not exist, and "the champion is
 * improving" is the exact misreading — a plausible, quotable one — that this repo's
 * rule about corroboration among runs of one harness exists to prevent.
 *
 * So: marks, no connecting line, no smoothing, no direction.
 *
 * The one thing that *is* comparable across runs is the **1500 baseline**, because
 * every tournament starts every hypothesis there. Above it means a target won more
 * than it lost in its own pool; below means it lost. That holds per run without
 * comparing runs to each other, so it is drawn as the reference hairline and the
 * marks are read against it rather than against each other.
 *
 * Inline SVG rather than a chart library. Recharts is ~500 KB and styles through
 * inline `stroke` props, which would put colour values outside the theme variables
 * where `tests/test_ui_theme.py` cannot see them and where the light theme cannot
 * reach them. Tailwind's `stroke-*` and `fill-*` classes go through the ramp like
 * everything else.
 */
import type { BoardObservation } from '../../api/types'
import { formatNumber, formatTimestamp } from '../../lib/utils'

const BASELINE = 1500
const W = 160
const H = 34
const PAD = 5

export function EloStrip({
  observations,
}: {
  observations: BoardObservation[] | null | undefined
}) {
  const points = (observations ?? []).filter(
    (o): o is BoardObservation & { elo: number } =>
      o.elo !== null && o.elo !== undefined,
  )

  if (points.length === 0) {
    return (
      <p className="text-2xs text-slate-500">
        No run recorded an Elo for this target.
      </p>
    )
  }

  // The scale spans the observations *and* the baseline, so 1500 is always on the
  // canvas. Without it, a target every run rated above 1500 would be drawn against
  // a floor of its own worst run and read as though it had been beaten.
  const values = [...points.map((p) => p.elo), BASELINE]
  const lo = Math.min(...values)
  const hi = Math.max(...values)
  const span = hi - lo || 1

  const y = (v: number) => PAD + (1 - (v - lo) / span) * (H - 2 * PAD)
  // Chronological left to right; a single observation sits in the middle rather
  // than pinned to the left edge, where it would imply a series that has only begun.
  const x = (i: number) =>
    points.length === 1
      ? W / 2
      : PAD + (i / (points.length - 1)) * (W - 2 * PAD)

  const baselineY = y(BASELINE)

  return (
    <div className="space-y-1">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width={W}
        height={H}
        role="img"
        aria-label={`Elo from ${points.length} ${
          points.length === 1 ? 'run' : 'runs'
        }, each from its own tournament and not a series: ${points
          .map((p) => `${p.run_id} ${Math.round(p.elo)}`)
          .join(', ')}. Every tournament starts at ${BASELINE}.`}
      >
        <line
          x1={0}
          x2={W}
          y1={baselineY}
          y2={baselineY}
          strokeDasharray="2 3"
          className="stroke-slate-700"
          strokeWidth={1}
        />
        {points.map((p, i) => (
          <circle
            key={`${p.run_id}-${p.target_id}`}
            cx={x(i)}
            cy={y(p.elo)}
            r={3}
            className={
              p.elo >= BASELINE ? 'fill-brand-400' : 'fill-slate-500'
            }
          >
            <title>{`${p.run_id} · Elo ${formatNumber(p.elo, 0)} · ${formatTimestamp(
              p.created_at,
            )}`}</title>
          </circle>
        ))}
      </svg>
      <p className="text-2xs leading-relaxed text-slate-500">
        One mark per run, against the {BASELINE} every tournament starts from. Not a
        series — each rating is comparative only within its own run.
      </p>
    </div>
  )
}
